import { isRetryable, type ApiError, type Result } from './api.ts';
import type { KV } from './guest.ts';

/**
 * 邀请关系：把邀请链接上的 `?ref=` 存下来，注册成功之后绑上去。
 *
 * ════════════════════════════════════════════════════════
 * 这件事以前整个没做，而它的失败是**完全无声的**
 * ════════════════════════════════════════════════════════
 * 后端一直是齐的：`POST /api/referral/bind`、`bindReferrer`（只能绑一次、
 * 不许填自己的码）、返佣阶梯、只追加的账本、对账，全都写好了也测过。
 * 缺的只有网页这一头 —— **没有任何地方读过 URL 上的 `?ref=`，
 * 也没有任何地方调过那条接口。**
 *
 * 所以用户把邀请链接发出去、朋友点进来注册、付钱，他一分返佣都拿不到，
 * 「我的邀请」那六张卡永远是 0。而这中间一个错都不报：
 * 他做完了该做的事，系统没记。这和「付了钱没开通」是同一类问题。
 *
 * ════════════════════════════════════════════════════════
 * 为什么判断全做成纯函数
 * ════════════════════════════════════════════════════════
 * 「该不该绑」「存的码过期了没」「这次失败该不该再试」三件事，
 * 一旦掺进 `Date.now()` 和 `localStorage` 就只能在真浏览器里验 ——
 * 而这里最难验的那条路（Google 整页跳走再跳回来）正好是最容易写错的。
 * 时间和已存的值一律从参数来，于是用假时钟、假存储就能把全部分支测透。
 * 读写 `localStorage` 只有本文件末尾那一层薄壳。
 *
 * ── 六个必须拍板的问题，答案和理由都在下面对应的常量 / 函数上 ──
 *   1. 存哪里   `REF_KEY`（localStorage，不是 React 状态也不是 sessionStorage）
 *   2. 存多久   `REF_TTL_MS`
 *   3. 谁赢     `captureRecord`（后点的赢）
 *   4. 老用户   `decideBind`（只在注册那一刻绑，和运营参数 ref_bind_at 一致）
 *   5. 重试     `MAX_TRIES` + `classifyBindError`
 *   6. 不可信   `normalizeRefCode`
 */

/* ══════════════════════ 1. 邀请码本身：不可信输入 ══════════════════════ */

/** 邀请链接上那个查询参数的名字 */
export const REF_PARAM = 'ref';

/**
 * 邀请码的字母表。和后端生成时用的那一份一致
 * （`packages/auth/src/bootstrap.ts` 的 `REF_ALPHABET`，
 * 去掉了 0/1/I/L/O/U 这些念出来会听错、抄下来会看错的字符）。
 *
 * **不做模糊纠正**（把 0 当 O、把 1 当 I）：码是从链接里原样来的，
 * 不是手打的，纠错只会让一个本来无效的码变成某个别人的有效码。
 */
const REF_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * 长度范围刻意**照抄服务端那条 zod**（`z.string().trim().min(4).max(32)`），
 * 而不是写死成现在的 8 位。
 *
 * 两种错法的代价不对等：
 *   前端卡死 8 位，哪天后端把码加长 → 邀请**静默地**再也绑不上，
 *   而那正是我们现在在修的这个 bug；
 *   前端放宽到服务端的范围，最坏情况是把一个不存在的码发过去，
 *   服务器回 404「这个邀请码不存在」—— 一次无害的请求，而且我们认得出是终态。
 */
const CODE_MIN = 4;
const CODE_MAX = 32;

const CODE_RE = new RegExp(`^[${REF_ALPHABET}]{${CODE_MIN},${CODE_MAX}}$`);

/**
 * 把 URL 上来的那串东西夹成一个邀请码，夹不出来就是 null。
 *
 * ════════════════════════════════════════════════════════
 * **绝不能把原样的字符串存进 localStorage。**
 * ════════════════════════════════════════════════════════
 * 它来自别人发的链接，`?ref=` 后面可以是任意内容：几 KB 的垃圾
 * （把存储撑满，用户的外观缓存跟着写不进去）、一段 HTML、
 * 一个 `javascript:`。我们存下来、以后还要显示它，
 * 所以**在进存储之前就夹好**：trim → 转大写 → 整串必须落在字母表里。
 *
 * 转大写是因为后端 `bindReferrer` 也转大写（它的测试专门测了
 * 小写加空格的码能绑上），用户从聊天软件里复制时经常连带空格。
 * 不在字母表里的字符**一律整串拒掉，不做剔除** ——
 * 剔除会把 `A<script>BCDEFGH` 悄悄变成一个合法的码，
 * 而「悄悄变成另一个值」在钱的事情上是不能接受的。
 */
export function normalizeRefCode(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  return CODE_RE.test(code) ? code : null;
}

/** 从 `?a=1&ref=XXXX` 里取出夹好的邀请码。取不到就是 null */
export function refCodeFromSearch(search: string): string | null {
  try {
    return normalizeRefCode(new URLSearchParams(search).get(REF_PARAM));
  } catch {
    /* URLSearchParams 对畸形输入是宽容的，但它不是契约 —— 这一句绝不能抛 */
    return null;
  }
}

/* ══════════════════════ 2. 存多久 ══════════════════════ */

/** localStorage 的键。和外观缓存同一套 `jyfk.` 前缀 */
export const REF_KEY = 'jyfk.ref';

/**
 * 存下来的码留 **30 天**。
 *
 * ── 为什么不是「这次浏览」──────────────────────────────
 * 他可能今天点进来看一眼就走，三天后才回来注册。只留一次会话（sessionStorage）
 * 的话这笔返佣就没了，而这正是用户抱怨的那件事。
 *
 * ── 为什么不是永久 ────────────────────────────────────
 * 半年前随手点过一个链接、今天自己搜过来注册，这不该算在那个人头上 ——
 * 返佣是给「真的把人带进来」的人的。30 天是返佣这行的惯例窗口，
 * 也刚好覆盖「看到 → 犹豫 → 回来注册」这段真实的决策时间。
 *
 * ── 一个要如实说的限制 ────────────────────────────────
 * Safari 的 ITP 会清掉「脚本写的存储」—— 七天内没再回来过这个站，
 * 这条记录就不在了。那是浏览器的策略，我们改不了；
 * 对那部分用户实际窗口是七天。
 */
export const REF_TTL_MS = 30 * 24 * 3600_000;

/**
 * 「账号是刚注册的」算多新：**24 小时**。
 *
 * 运营参数 `ref_bind_at` 的值是 `'signup'` —— 设计上就是注册那一刻绑。
 * 判断「这一次登录是不是注册」只能靠账号的注册时刻（`/me` 给的 `createdAt`）。
 *
 * 为什么窗口放到 24 小时，而不是几分钟：
 * `createdAt` 是**服务器时间**，`now` 是**这台电脑的时钟**，两者会差。
 * 差几分钟很常见（没对时的台式机），差几小时也见得到（时区设错会影响
 * 那些把本地时间当 UTC 用的系统）。窗口太窄的后果是一个真的刚注册完的人
 * 被判成老用户、返佣丢掉 —— 而窗口宽一点的代价只是「昨天注册、今天才点链接」
 * 的人也算上，那是个无害的偏差（他确实是个新人）。
 * 两种错法不对等，所以往宽的那边放。
 */
export const NEW_ACCOUNT_MS = 24 * 3600_000;

/**
 * 时钟容差。存下来的时刻比「现在」还晚这么多以内都接受。
 *
 * 用户把系统时间往前调过、或者两次读到的时钟不一致时，
 * `at` 会看起来在未来。超出容差就当这条记录是坏的（和 guest.ts 的草稿一样处理）。
 */
const CLOCK_SKEW_MS = 60_000;

/* ══════════════════════ 5. 重试几次 ══════════════════════ */

/**
 * 「值得重试的失败」最多攒几次，攒满就不再发请求了。
 *
 * ── 为什么要重试 ──────────────────────────────────────
 * 网络抖一下就永久丢掉一笔返佣，这不可接受。
 *
 * ── 为什么不能无限重试 ────────────────────────────────
 * 码本身就是错的话，重试一万次结果都一样 —— 那种失败我们认得出来
 * （见 `classifyBindError`），当场就把码扔了，一次都不重试。
 * 这个上限守的是另一件事：真的出了长时间故障时，不要每次打开网页都去撞一次。
 *
 * ── 重试是什么节奏 ────────────────────────────────────
 * **一次启动最多试一次**，不设定时器。
 * 定时器要管清理、还会在一堆人同时回来时一起撞上去；
 * 而记录本来就存着 30 天，下一次打开网页自然就是下一次重试 ——
 * 不急，也不会丢。
 *
 * 攒满之后记录**不删**：留着，界面才有话可说（「重新点一次那条邀请链接」），
 * 而重新点一次会把计数清零（见 `captureRecord`），用户自己就能把它救回来。
 */
export const MAX_TRIES = 5;

/* ══════════════════════ 存下来的那条记录 ══════════════════════ */

export interface RefRecord {
  /** 格式版本。以后改形状时旧记录整条丢掉，而不是拿一半字段去用 */
  v: 1;
  /** 已经夹过的邀请码 */
  code: string;
  /** 存下来的时刻（毫秒） */
  at: number;
  /** 已经攒了几次「值得重试」的失败 */
  tries: number;
  /**
   * 已经判定过「这个账号该绑这个码」、但还没绑成功时记下的用户 id。
   *
   * 有它才能把「网络抖了一下」救回来：第一次尝试是在注册那一刻发的，
   * 当时账号确实是新的；失败之后他三天后才回来，那时账号已经不「新」了，
   * 没有这个字段就会被判成老用户而永远不再试 —— 返佣还是丢了。
   * 记下这个 id 之后，后面的重试只认它，不再看账号新旧。
   */
  forUser?: string | undefined;
}

/**
 * 把存储里那串东西读成一条记录。读不出来、过期了、看起来被改过 → null。
 *
 * **纯函数**：`now` 从参数来，所以「过期了没」能用假时钟测。
 */
export function parseRecord(raw: string | null, now: number): RefRecord | null {
  let o: unknown = null;
  try {
    o = JSON.parse(raw ?? 'null');
  } catch {
    return null;
  }
  if (o === null || typeof o !== 'object') return null;
  const r = o as { v?: unknown; code?: unknown; at?: unknown; tries?: unknown; forUser?: unknown };
  if (r.v !== 1) return null;

  /* 存储里的东西可能被别的版本写过，也可能被人手改过 ——
     **再夹一遍邀请码**，不要信任自己存过的值 */
  const code = normalizeRefCode(typeof r.code === 'string' ? r.code : null);
  if (code === null) return null;

  const at = typeof r.at === 'number' && Number.isFinite(r.at) ? r.at : null;
  if (at === null) return null;
  if (now - at > REF_TTL_MS) return null;
  if (at - now > CLOCK_SKEW_MS) return null;

  const tries = typeof r.tries === 'number' && Number.isFinite(r.tries) && r.tries > 0
    ? Math.min(Math.floor(r.tries), MAX_TRIES)
    : 0;
  const forUser = typeof r.forUser === 'string' && r.forUser.length > 0 ? r.forUser : undefined;

  return { v: 1, code, at, tries, ...(forUser !== undefined ? { forUser } : {}) };
}

/* ══════════════════════ 3. 两个码撞上了，谁赢 ══════════════════════ */

/**
 * 又点了一条邀请链接。**后点的那个赢。**
 *
 * ── 为什么不是「保留第一个」──────────────────────────
 * 保留第一个有一种很难解释的失败：一个月前随手点过的链接，
 * 悄悄盖掉了今天真的把他拉进来的那个朋友 —— 而两边谁都看不出发生了什么，
 * 也没有任何办法纠正。后点的赢至少符合用户自己刚做的那个动作
 * （他刚点的是这条链接），也是返佣这行的默认口径（last click）。
 *
 * ── 重新点一次会把失败计数清零 ────────────────────────
 * 点链接是一个明确的人为动作，不可能变成死循环；
 * 而「试了几次都没成功，重新点一次链接」是我们唯一能给用户的自救办法。
 *
 * `forUser` 只在码没变时留着：码换了，之前那次「该绑」的判定就作废了。
 */
export function captureRecord(prev: RefRecord | null, code: string, now: number): RefRecord {
  const keepFor = prev !== null && prev.code === code ? prev.forUser : undefined;
  return { v: 1, code, at: now, tries: 0, ...(keepFor !== undefined ? { forUser: keepFor } : {}) };
}

/* ══════════════════════ 4. 这一刻该不该绑 ══════════════════════ */

export interface BindWho {
  /** 当前登录的用户 id。null = 还没登录（游客） */
  userId: string | null;
  /** 账号注册时刻（`/me` 给的 ISO 串，服务器时间）。null = 不知道 */
  accountCreatedAt: string | null;
  /** 账号上已经有邀请人了 */
  alreadyBound: boolean;
}

export type BindPlan =
  | { act: 'bind'; code: string }
  /** 这一刻不绑。码**留着**（它对这台浏览器上的下一次注册可能还有用） */
  | { act: 'hold'; why: 'no-code' | 'guest' | 'already-bound' | 'account-not-new' | 'gave-up' };

/**
 * 账号是不是「刚注册的」。
 *
 * 认不出注册时刻（字段没给、格式不认）时返回 **true**。
 * 这是有意的：两种错法里，「把新人当老用户」要赔掉一笔返佣，
 * 而「把老用户当新人」最多是多发一次请求 ——
 * 而且后端 `bindReferrer` 本身就守着「只能绑一次」，不会有人被抢走邀请人。
 */
export function isNewAccount(createdAt: string | null, now: number): boolean {
  if (createdAt === null) return true;
  const t = Date.parse(createdAt);
  if (!Number.isFinite(t)) return true;
  /* 注册时刻看起来在未来 = 两边时钟不一致，不能据此判他是老用户 */
  if (t > now) return true;
  return now - t <= NEW_ACCOUNT_MS;
}

/**
 * 这一刻该不该把存着的码绑上去。**纯函数，这是整件事的大脑。**
 *
 * ── 老用户点了别人的邀请链接会怎样：不绑 ──────────────
 * 运营参数 `ref_bind_at` 写的是 `'signup'`，所以只在注册那一刻绑。
 *
 * `bindReferrer` 本身只守「码存在」「不是自己」「只能绑一次」三件事，
 * 所以「只在注册那一阵子绑」这条规则在**接口那一层**另有一道闸
 * （`POST /api/referral/bind` 会拒掉注册超过 7 天的账号）——
 * 前端不是安全边界，绕过网页直接调接口的人要被那道闸挡住。
 *
 * 这里这一道窗口更窄（24 小时），作用不一样：它是为了**不白发请求**。
 * 两道窗口刻意不相等 —— 接口那道要宽到能容下「注册完隔几天才再打开网页」的重试。
 * 前端不自己另写「已经绑过了」那套规则：那一条交给后端的条件写入，
 * 这里只是不去撞它（撞了会拿到 409，白发一个请求、还得翻译一句错误）。
 */
export function decideBind(record: RefRecord | null, who: BindWho, now: number): BindPlan {
  if (record === null) return { act: 'hold', why: 'no-code' };
  /* 游客（带着 ?ref= 刚进来的人就是游客）：只存本地，**一个请求都不发**。
     api 那一层也会把写请求拦成 kind:'guest'，这里不发是为了不白跑一趟 */
  if (who.userId === null) return { act: 'hold', why: 'guest' };
  /* 已经有邀请人了：绑不上也改不了（后端只允许绑一次）。
     码不删 —— 这台浏览器上的下一次注册还可能用到它 */
  if (who.alreadyBound) return { act: 'hold', why: 'already-bound' };
  if (record.tries >= MAX_TRIES) return { act: 'hold', why: 'gave-up' };
  /* 之前已经判过「该绑」，只是没成功 → 继续试，不再看账号新旧 */
  if (record.forUser === who.userId) return { act: 'bind', code: record.code };
  if (!isNewAccount(who.accountCreatedAt, now)) return { act: 'hold', why: 'account-not-new' };
  return { act: 'bind', code: record.code };
}

/* ══════════════════════ 失败了：再试，还是扔掉 ══════════════════════ */

/**
 * 这次失败值得再试吗。
 *
 * `terminal` = 这个码永远绑不上了（不存在、是他自己的、已经绑过、格式被拒）——
 * 扔掉，一次都不重试。
 * `retry`    = 网络、超时、服务器 5xx、限流 —— 留着，下次打开网页再试。
 *
 * `guest` 和 `unauthorized` 归到 `retry`：前者是请求**根本没发出去**
 * （游客拦截），后者是会话没了 —— 两种都不是「码不对」，不该因此扔掉码。
 */
export function classifyBindError(e: ApiError): 'retry' | 'terminal' {
  if (e.kind === 'guest' || e.kind === 'unauthorized') return 'retry';
  return isRetryable(e) ? 'retry' : 'terminal';
}

/* ══════════════════════ 要跟用户交代什么 ══════════════════════ */

/**
 * 绑定这件事的结果，给界面交代用。null = 这次打开没有这件事发生。
 *
 * 绑成功和绑失败**都要有交代** —— 但失败**绝不能影响登录**：
 * 这里只是一个状态，不抛错、不挡启动、不画红屏。
 */
export type RefBindState =
  /** 绑上了 */
  | { phase: 'bound' }
  /** 这次没成，码留着，下次打开再试 */
  | { phase: 'pending' }
  /** 终态：`code` = 这个码没用上；`gave-up` = 试了几次都不行 */
  | { phase: 'failed'; why: 'code' | 'gave-up' }
  /** 没去绑，而且是有道理的（已经有邀请人 / 不是注册那一刻） */
  | { phase: 'skipped'; why: 'already-bound' | 'account-not-new' };

/* ══════════════════════ 薄壳：读写 localStorage ══════════════════════ */

/**
 * 这台浏览器的 localStorage，拿不到就是 null（隐身模式、被策略禁掉、
 * 或者压根没有 window 的环境 —— 测试就跑在 node 里）。
 *
 * **存在 localStorage，不是 React 状态，也不是 sessionStorage。**
 *   React 状态：Google 登录是**整页跳走**（去 accounts.google.com 再跳回来），
 *               回来是一个全新的文档加载，内存里什么都不剩 —— 这条路最常用，
 *               也正是最容易漏掉的那条。
 *   sessionStorage：能活过整页跳转，但活不过「今天看一眼、三天后再来注册」。
 * 两条都不够，所以用 localStorage。
 *
 * 也**不用 cookie**：cookie 会跟着每一个请求发到服务器上，
 * 而这个码只有网页自己要用；后端那条接口要的是显式的一次调用，不读 cookie。
 */
export function localKV(): KV | null {
  try {
    const s = localStorage;
    return {
      getItem: k => s.getItem(k),
      setItem: (k, v) => { s.setItem(k, v); },
      removeItem: k => { s.removeItem(k); }
    };
  } catch {
    return null;
  }
}

/** 读出存着的记录。过期或者坏了顺手清掉（省得每次启动都解析一遍垃圾） */
export function readRef(storage: KV | null, now: number): RefRecord | null {
  if (storage === null) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(REF_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  const rec = parseRecord(raw, now);
  if (rec === null) clearRef(storage);
  return rec;
}

export function writeRef(storage: KV | null, rec: RefRecord): void {
  if (storage === null) return;
  try {
    storage.setItem(REF_KEY, JSON.stringify(rec));
  } catch {
    /* 隐身模式或者存储满了。存不下只是这笔邀请关系记不住，
       **不能因此让页面出错** —— 那是浏览器的限制，不是用户的错 */
  }
}

export function clearRef(storage: KV | null): void {
  if (storage === null) return;
  try {
    storage.removeItem(REF_KEY);
  } catch {
    /* 同上 */
  }
}

/**
 * 落地页那一下：把链接上的码存下来。
 *
 * **一个请求都不发**（带着 `?ref=` 进来的人就是游客），只写本地。
 *
 * 返回的是**真的存住了的那条记录** —— 写完再读一遍。
 * 不这么做的话，隐身模式下它会返回一条其实没存下来的记录，
 * 而调用方照着它说一句「记下了」就是在骗人。没码、存不下都返回 null。
 */
export function captureRef(storage: KV | null, search: string, now: number): RefRecord | null {
  const code = refCodeFromSearch(search);
  if (code === null) return null;
  writeRef(storage, captureRecord(readRef(storage, now), code, now));
  return readRef(storage, now);
}

/* ══════════════════════ 整件事串起来 ══════════════════════ */

/** 「这一刻不绑」要不要跟用户说一句，说哪一句 */
function holdState(why: Extract<BindPlan, { act: 'hold' }>['why']): RefBindState | null {
  switch (why) {
    /* 绝大多数人属于这两种：没点过邀请链接、或者还没登录。什么都不用说 */
    case 'no-code':
    case 'guest':
      return null;
    /* 这两种要说一句 —— 用户问「我点了链接为什么没算」时，只有这里答得上来 */
    case 'already-bound':
    case 'account-not-new':
      return { phase: 'skipped', why };
    case 'gave-up':
      return { phase: 'failed', why: 'gave-up' };
  }
}

export interface BindDeps {
  /** 发那一个请求。注入进来是为了能在 node 里把整条流程测完，不用浏览器 */
  /** 第二个参数是点链接的时刻（毫秒）：服务端按运营参数 ref_cookie_days 判过没过期 */
  post: (code: string, capturedAt?: number) => Promise<Result<unknown>>;
  storage: KV | null;
  now: number;
}

/**
 * 登录成功之后调一次：该绑就绑，绑完把码清掉，失败按类型决定留不留。
 *
 * 返回要跟用户交代的那句话的依据（`RefBindState`），null = 没什么要说的。
 *
 * 存储出的任何问题这里全兜住了（见上面那三个薄壳），但**注入的 `post`
 * 自己抛异常时这个 Promise 会 reject** —— 调用方必须包一层 try：
 * 见 `state/app.tsx` 的 `bindReferral`。那一层才是「登录永远优先」的保证。
 */
export async function runReferralBind(deps: BindDeps, who: BindWho): Promise<RefBindState | null> {
  const { storage, now } = deps;
  const rec = readRef(storage, now);
  const plan = decideBind(rec, who, now);

  if (plan.act === 'hold') return holdState(plan.why);

  /* 走到这里 rec 一定不是 null（decideBind 只在有记录时才给 bind），
     但类型上还是要收一下 —— 不写这一句的话下面得用非空断言 */
  if (rec === null) return null;

  /* 先把「这个账号该绑」记进存储，**再发请求**。
     顺序反了的话：请求发出去、网络断了、他三天后回来，
     那时账号已经不算新了，于是永远不会再试 —— 返佣就这么丢了。
     （`userId` 在这里一定不是 null：decideBind 对游客给的是 hold） */
  const claimed: RefRecord = who.userId === null ? rec : { ...rec, forUser: who.userId };
  if (claimed.forUser !== rec.forUser) writeRef(storage, claimed);

  const r = await deps.post(plan.code, rec.at);
  if (r.ok) {
    /* 用掉了就清掉：留着只会在下一次启动时再撞一个 409 */
    clearRef(storage);
    return { phase: 'bound' };
  }

  if (classifyBindError(r.error) === 'terminal') {
    /* 码本身没用（不存在 / 是他自己的 / 这个账号已经绑过）—— 扔掉，别再试 */
    clearRef(storage);
    return { phase: 'failed', why: 'code' };
  }

  const tries = Math.min(claimed.tries + 1, MAX_TRIES);
  writeRef(storage, { ...claimed, tries });
  return tries >= MAX_TRIES ? { phase: 'failed', why: 'gave-up' } : { phase: 'pending' };
}
